import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MoreHorizOutlinedIcon from '@mui/icons-material/MoreHorizOutlined';
import type { SxProps } from '@mui/material';
import { IconButton, Stack, styled, Typography } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import { type FC, useMemo } from 'react';

import type { InformativeMessage } from '@/src/shared/types';
import { InformativeMessageStatus } from '@/src/shared/types';
import { primaryOrange } from '@/src/styles/theme/lightThemeOptions';

import { ParagraphStyle } from './styles';

const SharedItemStackStyle: SxProps = {
    flexDirection: 'row',
    alignItems: 'center',
    '& svg': {
        fontSize: '20px',
    },
    [`& .${InformativeMessageStatus.COMPLETED}`]: {
        color: '#13A10E',
    },
    [`& .${InformativeMessageStatus.ERROR}`]: {
        color: '#D32F2F',
    },
    '& .MuiTypography-root': {
        ...ParagraphStyle,
        opacity: 0.8,
    },
    '&.latest-item > .MuiTypography-root': {
        fontWeight: '500',
    },
};

const ItemStack = styled(Stack)({
    gap: '16px',
    '& .indent-placeholder': {
        minWidth: '20px',
        minHeight: '20px',
    },
    ...SharedItemStackStyle,
});

interface InformativeMessageItemProps {
    informativeMessage: InformativeMessage;
    isInformativePanelOpen: boolean;
    onToggleInformativePanelOpen: () => void;
}

const InformativeProgressIconBox = styled('div')({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '20px',
    height: '20px',
    '& .inner-box': {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        borderRadius: '50%',
        backgroundColor: primaryOrange,
        width: '16px',
        height: '16px',
    },
    '& .MuiCircularProgress-root': {
        color: '#fff',
        '& circle': {
            strokeWidth: '6px',
        },
    },
});

const InformativeProgressIcon = () => (
    <InformativeProgressIconBox>
        <div className="inner-box">
            <CircularProgress size={10} />
        </div>
    </InformativeProgressIconBox>
);

const StatusIconMap: Record<InformativeMessageStatus, FC> = {
    [InformativeMessageStatus.IN_PROGRESS]: () => <InformativeProgressIcon />,
    [InformativeMessageStatus.COMPLETED]: () => (
        <CheckCircleIcon className={InformativeMessageStatus.COMPLETED} />
    ),
    [InformativeMessageStatus.WAITING]: () => (
        <MoreHorizOutlinedIcon className={InformativeMessageStatus.WAITING} />
    ),
    [InformativeMessageStatus.ERROR]: () => (
        <ErrorOutlineIcon className={InformativeMessageStatus.ERROR} />
    ),
};

export const LatestInformativeMessageItem = ({
    informativeMessage,
    isInformativePanelOpen,
    onToggleInformativePanelOpen,
}: InformativeMessageItemProps) => {
    const { status, message } = informativeMessage;
    const StatusIcon = StatusIconMap[status];
    return (
        <ItemStack className="latest-item">
            <StatusIcon />
            <Typography variant="body1">{message}</Typography>
            <IconButton onClick={onToggleInformativePanelOpen}>
                {isInformativePanelOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
        </ItemStack>
    );
};

interface InformativeMessagePanelProps {
    informativeMessageMap: Record<string, InformativeMessage>;
}

const orderInformativeMessages = (informativeMessages: InformativeMessage[]) => {
    const topRows = informativeMessages.filter((row) => !row.parentId);
    const subRows = informativeMessages.filter((row) => row.parentId);
    const orderedList: InformativeMessage[] = [];
    topRows.forEach((topRow) => {
        orderedList.push(topRow);
        subRows.forEach((subRow) => {
            if (subRow.parentId === topRow.id) {
                orderedList.push(subRow);
            }
        });
    });
    return orderedList;
};

export const InformativeMessagePanel = ({
    informativeMessageMap,
}: InformativeMessagePanelProps) => {
    const informativeMessages = useMemo(
        () => orderInformativeMessages(Object.values(informativeMessageMap)),
        [informativeMessageMap]
    );
    return (
        <Stack>
            {informativeMessages.map((informativeMessage) => {
                const { status, message, parentId, subInstructions } = informativeMessage;
                const StatusIcon = StatusIconMap[status];
                const behindParentPlaceholder = parentId && <div className="indent-placeholder" />;
                return (
                    <Stack key={informativeMessage.id}>
                        <ItemStack>
                            {behindParentPlaceholder}
                            <StatusIcon />
                            <Typography variant="body1">{message}</Typography>
                        </ItemStack>
                        {subInstructions && (
                            <ItemStack>
                                {behindParentPlaceholder}
                                <div className="indent-placeholder" />
                                {subInstructions.map((subInstruction) => (
                                    <Typography key={subInstruction} variant="body1">
                                        - {subInstruction}
                                    </Typography>
                                ))}
                            </ItemStack>
                        )}
                    </Stack>
                );
            })}
        </Stack>
    );
};
